// AGC: ConsultationRequest - Form for creating a consultation request
// AGC: Allows farmer to describe problem and set budget

import React, { useState } from 'react'
import { 
  Send, 
  Camera, 
  AlertTriangle,
  Loader2,
  Check,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type ConsultationType,
  type ConsultationUrgency,
  CONSULTATION_TYPE_INFO,
  URGENCY_INFO,
} from '../../types/advisor'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'

interface ConsultationRequestProps {
  consultantId?: string
  consultationType?: ConsultationType
  onNavigate: (screen: Screen, params?: Record<string, unknown>) => void
  onBack: () => void
}

const BACKEND_URL = getBackendUrl()

/**
 * AGC: Consultation Request Component
 */
export const ConsultationRequest: React.FC<ConsultationRequestProps> = ({
  consultantId,
  consultationType,
  onNavigate,
  onBack,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  // AGC: Form state
  const [type, setType] = useState<ConsultationType>(consultationType || 'quick_question')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [urgency, setUrgency] = useState<ConsultationUrgency>('medium')
  const [budgetMin, setBudgetMin] = useState('')
  const [budgetMax, setBudgetMax] = useState('')
  
  // AGC: UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const typeInfo = CONSULTATION_TYPE_INFO[type]

  /**
   * AGC: Handle form submission
   */
  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('Preencha o título e a descrição')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/advisors/consultations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farmerId: user?.uid,
          farmerName: user?.displayName || user?.profile?.displayName,
          type,
          title,
          description,
          urgency,
          budget: {
            min: budgetMin ? parseFloat(budgetMin) : typeInfo.priceRange.min,
            max: budgetMax ? parseFloat(budgetMax) : typeInfo.priceRange.max,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
        setTimeout(() => {
          onNavigate('consultations' as Screen)
        }, 2000)
      } else {
        setError(data.error || 'Erro ao criar consulta')
      }
    } catch (err) {
      console.error('Error creating consultation:', err)
      setError('Erro de conexão')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Consulta Criada!
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Você receberá propostas de consultores em breve.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('advisor.requestConsultation') || 'Solicitar Consulta'}
        onBack={onBack}
      />

      <div className="p-4 pb-32 space-y-6">
        {/* AGC: Consultation type */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.consultationType') || 'Tipo de Consulta'}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(CONSULTATION_TYPE_INFO) as ConsultationType[]).map(t => {
              const info = CONSULTATION_TYPE_INFO[t]
              const isSelected = type === t
              return (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`p-3 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-500'
                      : 'bg-gray-50 dark:bg-slate-700 border-2 border-transparent'
                  }`}
                >
                  <p className={`font-medium text-sm ${
                    isSelected ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {info.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    R$ {info.priceRange.min}-{info.priceRange.max}
                  </p>
                </button>
              )
            })}
          </div>
          
          {/* AGC: Selected type info */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              <strong>{typeInfo.name}:</strong> {typeInfo.description}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
              Entrega: {typeInfo.deliverable} • Prazo: {typeInfo.responseTime}
            </p>
          </div>
        </GlassCard>

        {/* AGC: Title */}
        <GlassCard>
          <label className="block font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('advisor.title') || 'Título'} *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Manchas nas folhas da soja"
            className="w-full px-4 py-3 bg-gray-100 dark:bg-slate-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </GlassCard>

        {/* AGC: Description */}
        <GlassCard>
          <label className="block font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('advisor.description') || 'Descrição'} *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descreva seu problema em detalhes..."
            rows={5}
            className="w-full px-4 py-3 bg-gray-100 dark:bg-slate-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Quanto mais detalhes, melhor será a resposta do consultor.
          </p>
        </GlassCard>

        {/* AGC: Urgency */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.urgency') || 'Urgência'}
          </h3>
          <div className="flex gap-2">
            {(Object.keys(URGENCY_INFO) as ConsultationUrgency[]).map(u => {
              const info = URGENCY_INFO[u]
              const isSelected = urgency === u
              return (
                <button
                  key={u}
                  onClick={() => setUrgency(u)}
                  className={`flex-1 p-3 rounded-xl text-center transition-all ${
                    isSelected
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-500'
                      : 'bg-gray-50 dark:bg-slate-700 border-2 border-transparent'
                  }`}
                >
                  <p className={`font-medium text-sm ${
                    isSelected ? 'text-emerald-700 dark:text-emerald-400' : info.color
                  }`}>
                    {info.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {info.maxResponseTime}
                  </p>
                </button>
              )
            })}
          </div>
        </GlassCard>

        {/* AGC: Budget */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.budget') || 'Orçamento'} (opcional)
          </h3>
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Mínimo</label>
              <input
                type="number"
                value={budgetMin}
                onChange={(e) => setBudgetMin(e.target.value)}
                placeholder={String(typeInfo.priceRange.min)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-xl text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <span className="text-gray-400 mt-4">-</span>
            <div className="flex-1">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Máximo</label>
              <input
                type="number"
                value={budgetMax}
                onChange={(e) => setBudgetMax(e.target.value)}
                placeholder={String(typeInfo.priceRange.max)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-xl text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Preço sugerido: R$ {typeInfo.priceRange.min} - {typeInfo.priceRange.max}
          </p>
        </GlassCard>

        {/* AGC: Attachments placeholder */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.attachments') || 'Anexos'} (opcional)
          </h3>
          <button className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400 hover:border-emerald-500 hover:text-emerald-600 transition-colors">
            <Camera className="w-8 h-8" />
            <span className="text-sm">Adicionar fotos</span>
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            Fotos ajudam no diagnóstico
          </p>
        </GlassCard>

        {/* AGC: Error message */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* AGC: Submit button */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-safe-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !title.trim() || !description.trim()}
          className="w-full py-4 bg-emerald-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Enviar Solicitação
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default ConsultationRequest

